import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, ChevronDown, ChevronRight, CheckCircle2, XCircle, Clock, ArrowRight, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LoadingState } from '@/components/ui/loading-state'
import { cn } from '@/lib/utils'
import { traceMessage } from '../queries/messageTrace'
import type { MessageTraceHop, TraceChannel, TopicMessage } from '../types/topology'

// ─── Constants ────────────────────────────────────────────────────────────────

const CHANNELS: { label: string; value: TraceChannel; contactLabel: string; placeholder: string }[] = [
  { label: 'All Channels', value: 'ALL',      contactLabel: 'Email or Phone', placeholder: 'anshul@example.com  or  9876543210' },
  { label: 'RCS',          value: 'RCS',      contactLabel: 'Phone Number',   placeholder: '9876543210  or  +919876543210' },
  { label: 'SMS',          value: 'SMS',      contactLabel: 'Phone Number',   placeholder: '9876543210  or  +919876543210' },
  { label: 'Email',        value: 'EMAIL',    contactLabel: 'Email Address',  placeholder: 'user@example.com' },
  { label: 'WhatsApp',     value: 'WHATSAPP', contactLabel: 'Phone Number',   placeholder: '9876543210  or  +919876543210' },
]

const PATH_SECTIONS: { path: string; title: string; desc: string }[] = [
  { path: 'campaign', title: 'Campaign Path',    desc: 'Direct campaign routing' },
  { path: 'journey',  title: 'Journey Path',     desc: 'Journey-triggered delivery' },
  { path: 'delivery', title: 'Delivery Topics',  desc: 'Channel-specific send queues' },
  { path: 'status',   title: 'Message Status',   desc: 'Delivery outcome reporting' },
]

// ─── Helpers ─────────────────────────────────────────────────────────────────

function deepParse(value: unknown): unknown {
  if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
    try { return deepParse(JSON.parse(value)) } catch { /* not JSON */ }
  }
  if (Array.isArray(value)) return value.map(deepParse)
  if (value && typeof value === 'object')
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, deepParse(v)])
    )
  return value
}

function fmtTime(ms: number) {
  return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function pick(obj: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) if (obj[k] != null) return String(obj[k])
  return ''
}

// ─── Message mini-row ─────────────────────────────────────────────────────────

function MiniMessage({ msg, index }: { msg: TopicMessage; index: number }) {
  const [open, setOpen] = useState(false)
  const isEven = index % 2 === 0
  const parsed = useMemo(() => deepParse(msg.value), [msg.value])

  const o = (msg.value && typeof msg.value === 'object' && !Array.isArray(msg.value))
    ? msg.value as Record<string, unknown> : {}

  const to       = pick(o, 'to')
  const channel  = pick(o, 'channelType', 'channel_type', 'channel', 'category')
  const msgType  = pick(o, 'messageType', 'message_type', 'type')
  const vendor   = pick(o, 'vendor')
  const status   = pick(o, 'status')
  const testMode = pick(o, 'testMode')

  return (
    <>
      <tr
        className={cn(
          'cursor-pointer hover:bg-white/60 transition-colors text-[11px]',
          isEven ? 'bg-white/30' : 'bg-transparent',
          open && '!bg-white/70'
        )}
        onClick={() => setOpen(v => !v)}
      >
        <td className="pl-2 pr-1 py-1.5 w-4">
          {open
            ? <ChevronDown  className="h-3 w-3 text-gray-400" />
            : <ChevronRight className="h-3 w-3 text-gray-300" />}
        </td>
        <td className="px-2 py-1.5 font-mono text-gray-500 whitespace-nowrap">
          {fmtTime(msg.timestamp)}
        </td>
        <td className="px-2 py-1.5 font-mono text-gray-400 whitespace-nowrap">
          p{msg.partition}/{msg.offset}
        </td>
        <td className="px-2 py-1.5">
          <div className="flex flex-wrap gap-1">
            {to       && <Chip color="bg-blue-100 text-blue-700">→ {to}</Chip>}
            {channel  && <Chip color="bg-cyan-100 text-cyan-700">{channel}</Chip>}
            {status   && (
              <Chip color={
                status === 'DELIVERED'        ? 'bg-green-100 text-green-700'
                : status.startsWith('FAIL')   ? 'bg-red-100 text-red-700'
                : 'bg-gray-100 text-gray-600'
              }>{status}</Chip>
            )}
            {msgType  && <Chip color="bg-gray-100 text-gray-600">{msgType}</Chip>}
            {vendor   && <Chip color="bg-teal-100 text-teal-700">{vendor}</Chip>}
            {testMode === 'True' && <Chip color="bg-yellow-100 text-yellow-700">TEST</Chip>}
          </div>
        </td>
        <td className="px-2 py-1.5 font-mono text-gray-500 max-w-xs truncate">
          {msg.key ?? ''}
        </td>
      </tr>
      {open && (
        <tr className={isEven ? 'bg-white/30' : 'bg-transparent'}>
          <td colSpan={5} className="px-4 pb-3 pt-0">
            <pre className="text-[10px] bg-gray-950 text-green-300 p-3 rounded-lg overflow-auto max-h-64 font-mono leading-relaxed border border-gray-800">
              {JSON.stringify(parsed, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  )
}

function Chip({ children, color }: { children: React.ReactNode; color: string }) {
  return <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium', color)}>{children}</span>
}

// ─── Hop card ─────────────────────────────────────────────────────────────────

function HopCard({ hop }: { hop: MessageTraceHop }) {
  const [open, setOpen] = useState(false)

  return (
    <div className={cn(
      'rounded-xl border overflow-hidden min-w-[180px] flex-1',
      hop.found
        ? 'border-green-200 bg-green-50/70'
        : 'border-dashed border-gray-300 bg-gray-50/60'
    )}>
      <div
        className={cn('px-3 py-2.5', hop.found && 'cursor-pointer hover:bg-green-100/50 transition-colors')}
        onClick={() => hop.found && setOpen(v => !v)}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[11px] font-mono font-semibold text-gray-800 truncate">{hop.topic}</p>
            <p className="text-[10px] text-gray-500 mt-0.5">{hop.stage}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0 mt-0.5">
            {hop.found ? (
              <>
                <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                <span className="text-[10px] font-semibold text-green-700">{hop.matchCount}</span>
                {open
                  ? <ChevronDown  className="h-3 w-3 text-green-400 ml-0.5" />
                  : <ChevronRight className="h-3 w-3 text-green-400 ml-0.5" />}
              </>
            ) : (
              <XCircle className="h-3.5 w-3.5 text-gray-300" />
            )}
          </div>
        </div>
      </div>

      {open && hop.found && hop.messages.length > 0 && (
        <div className="border-t border-green-200">
          <table className="w-full">
            <thead>
              <tr className="bg-green-100/60">
                <th className="w-4" />
                <th className="px-2 py-1 text-left text-[9px] font-semibold text-gray-500 uppercase">Time</th>
                <th className="px-2 py-1 text-left text-[9px] font-semibold text-gray-500 uppercase">P/Off</th>
                <th className="px-2 py-1 text-left text-[9px] font-semibold text-gray-500 uppercase">Details</th>
                <th className="px-2 py-1 text-left text-[9px] font-semibold text-gray-500 uppercase">Key</th>
              </tr>
            </thead>
            <tbody>
              {hop.messages.map((m, i) => (
                <MiniMessage key={`${m.partition}-${m.offset}`} msg={m} index={i} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ─── Path section ─────────────────────────────────────────────────────────────

function PathSection({ title, desc, hops }: {
  title: string; desc: string; hops: MessageTraceHop[]
}) {
  if (hops.length === 0) return null
  const foundCount = hops.filter(h => h.found).length

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/80">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
          <p className="text-[10px] text-gray-400">{desc}</p>
        </div>
        <span className={cn(
          'text-xs font-medium px-2 py-0.5 rounded-full',
          foundCount > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
        )}>
          {foundCount}/{hops.length} found
        </span>
      </div>
      <div className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          {hops.map((hop, i) => (
            <div key={hop.topic} className="flex items-center gap-2 flex-1 min-w-[200px]">
              <HopCard hop={hop} />
              {i < hops.length - 1 && (
                <ArrowRight className="h-4 w-4 text-gray-300 shrink-0" />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Summary bar ─────────────────────────────────────────────────────────────

function SummaryBar({ hops, searchedAt, contact, campaignId, channel }: {
  hops: MessageTraceHop[]; searchedAt: number; contact: string; campaignId: string; channel: string
}) {
  const total     = hops.length
  const found     = hops.filter(h => h.found).length
  const allFound  = found === total
  const noneFound = found === 0

  return (
    <div className={cn(
      'rounded-xl border px-4 py-3 flex flex-wrap items-center gap-4',
      allFound  ? 'bg-green-50 border-green-200'
      : noneFound ? 'bg-red-50 border-red-200'
      : 'bg-amber-50 border-amber-200'
    )}>
      <div className="flex items-center gap-2">
        {allFound  ? <CheckCircle2 className="h-5 w-5 text-green-500" />
        : noneFound ? <XCircle       className="h-5 w-5 text-red-400" />
        : <Clock       className="h-5 w-5 text-amber-500" />}
        <span className={cn(
          'text-sm font-semibold',
          allFound ? 'text-green-800' : noneFound ? 'text-red-700' : 'text-amber-800'
        )}>
          {noneFound ? 'Not found in any topic'
          : allFound  ? 'Found across all stages'
          : `Found in ${found} of ${total} stages`}
        </span>
      </div>
      <div className="flex flex-wrap gap-3 text-xs text-gray-600">
        <span><span className="font-medium">contact</span> {contact}</span>
        {campaignId && <span><span className="font-medium">campaignId</span> {campaignId}</span>}
        <span><span className="font-medium">channel</span> {channel}</span>
        <span className="text-gray-400">searched {new Date(searchedAt).toLocaleTimeString()}</span>
      </div>
    </div>
  )
}

// ─── Trace hint ───────────────────────────────────────────────────────────────

function TraceHint() {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3">
      <Info className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />
      <div className="text-xs text-blue-700 space-y-1">
        <p className="font-semibold">How tracing works</p>
        <ul className="space-y-0.5 text-blue-600">
          <li><span className="font-medium">Delivery topics</span> — searched directly by email / phone (<code className="bg-blue-100 px-1 rounded">to</code> field)</li>
          <li><span className="font-medium">Message Status</span> — matched by <code className="bg-blue-100 px-1 rounded">messageId</code> extracted from matched deliveries</li>
          <li><span className="font-medium">Campaign &amp; Journey events</span> — matched by <code className="bg-blue-100 px-1 rounded">executionId</code> / <code className="bg-blue-100 px-1 rounded">journeyId</code> extracted from delivery; add Campaign ID to improve upstream coverage</li>
        </ul>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function MessageTracePage() {
  const [contact,     setContact]     = useState('')
  const [campaignId,  setCampaignId]  = useState('')
  const [executionId, setExecutionId] = useState('')
  const [channel,     setChannel]     = useState<TraceChannel>('ALL')
  const [submitted,   setSubmitted]   = useState<null | {
    contact: string; campaignId: string; executionId: string; channel: string
  }>(null)

  const channelMeta = CHANNELS.find(c => c.value === channel) ?? CHANNELS[0]

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['message-trace', submitted],
    queryFn:  () => traceMessage({
      contact:     submitted!.contact,
      campaignId:  submitted!.campaignId  || undefined,
      executionId: submitted!.executionId || undefined,
      channel:     submitted!.channel,
    }),
    enabled:   !!submitted,
    staleTime: 0,
    retry:     0,
  })

  const hopsByPath = useMemo(() => {
    if (!data) return {}
    return PATH_SECTIONS.reduce((acc, s) => {
      acc[s.path] = data.hops.filter(h => h.path === s.path)
      return acc
    }, {} as Record<string, MessageTraceHop[]>)
  }, [data])

  const handleTrace = () => {
    if (!contact.trim()) return
    setSubmitted({ contact: contact.trim(), campaignId: campaignId.trim(),
      executionId: executionId.trim(), channel })
  }

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleTrace()
  }

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Search className="h-5 w-5 text-blue-500" />
          Message Trace
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Trace a message end-to-end by email or phone — delivery topics · status · campaign &amp; journey events
        </p>
      </div>

      {/* Search form */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">

        {/* Channel selector — first, determines contact label/placeholder */}
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
            Channel
          </label>
          <div className="flex gap-1 flex-wrap">
            {CHANNELS.map(ch => (
              <button
                key={ch.value}
                onClick={() => setChannel(ch.value)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors',
                  channel === ch.value
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300 hover:text-blue-600'
                )}
              >
                {ch.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          {/* contact — primary, channel-aware label & placeholder */}
          <div className="flex-1 min-w-[260px]">
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              {channelMeta.contactLabel} <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={contact}
              onChange={e => setContact(e.target.value)}
              onKeyDown={handleKey}
              placeholder={channelMeta.placeholder}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>

          {/* campaignId */}
          <div className="flex-1 min-w-[180px]">
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Campaign ID <span className="text-gray-300">(optional — improves coverage)</span>
            </label>
            <input
              type="text"
              value={campaignId}
              onChange={e => setCampaignId(e.target.value)}
              onKeyDown={handleKey}
              placeholder="1824018467061890"
              className="w-full px-3 py-2 text-sm font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>

          {/* executionId */}
          <div className="flex-1 min-w-[180px]">
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Execution ID <span className="text-gray-300">(optional)</span>
            </label>
            <input
              type="text"
              value={executionId}
              onChange={e => setExecutionId(e.target.value)}
              onKeyDown={handleKey}
              placeholder="1824022416859833"
              className="w-full px-3 py-2 text-sm font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
        </div>

        <Button
          onClick={handleTrace}
          disabled={!contact.trim() || isLoading}
          className="px-5"
        >
          {isLoading
            ? <><span className="mr-2 h-3.5 w-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin inline-block" />Tracing…</>
            : <><Search className="h-4 w-4 mr-1.5" />Trace</>}
        </Button>
      </div>

      {/* Loading */}
      {isLoading && <LoadingState />}

      {/* Error */}
      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
          {error instanceof Error ? error.message : 'Trace failed'}
        </div>
      )}

      {/* Results */}
      {data && !isLoading && (
        <>
          <SummaryBar
            hops={data.hops}
            searchedAt={data.searchedAt}
            contact={data.contact}
            campaignId={data.campaignId ?? ''}
            channel={data.channel}
          />

          {PATH_SECTIONS.map(s => (
            <PathSection
              key={s.path}
              title={s.title}
              desc={s.desc}
              hops={hopsByPath[s.path] ?? []}
            />
          ))}
        </>
      )}

      {/* Empty state with hint */}
      {!submitted && !isLoading && (
        <div className="space-y-4">
          <TraceHint />
          <div className="bg-gray-50 border border-dashed border-gray-200 rounded-xl p-10 text-center">
            <Search className="h-8 w-8 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-500">Enter an email or phone number to start tracing</p>
            <p className="text-xs text-gray-400 mt-1">
              Delivery topics searched first · campaign &amp; journey context resolved automatically
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
